/**
 * Prompt-cache breakpoints for Anthropic Messages requests.
 *
 * Three breakpoints are placed, in the `tools` -> `system` -> `messages` order
 * Anthropic reads a prompt as one prefix:
 *
 *  1. the last tool definition, which caches the whole `tools` array,
 *  2. the system prompt,
 *  3. a rolling breakpoint on the last cacheable block of the transcript.
 *
 * (3) only pays off on an append-only transcript: the prefix behind the
 * breakpoint is then unchanged next turn, and the entry this turn writes is
 * found by the lookback Anthropic runs back from the following breakpoint.
 * `cache_control` markers are outside the thinking-block prefix check, so
 * moving the rolling breakpoint never invalidates replayed thinking.
 *
 * The fourth slot stays free deliberately: exceeding the limit fails the
 * request rather than degrading one turn.
 */

type AnthropicContentBlock = Record<string, unknown>;

/** Anthropic rejects a request carrying more breakpoints than this with a 400. */
export const MAX_CACHE_BREAKPOINTS = 4;

const EPHEMERAL_CACHE_CONTROL = { type: "ephemeral" } as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Whether Anthropic accepts a `cache_control` on this block. Thinking blocks
 * cannot be marked directly (they are still cached as part of their turn), and
 * empty text blocks are not cacheable.
 */
function acceptsCacheControl(block: unknown): boolean {
  if (!isRecord(block)) return false;
  const type = typeof block.type === "string" ? block.type : "";
  if (type === "thinking" || type === "redacted_thinking") return false;
  if (type === "text") return typeof block.text === "string" && block.text.trim() !== "";
  return type !== "";
}

function markedWithCacheControl(block: Record<string, unknown>): AnthropicContentBlock {
  return { ...block, cache_control: EPHEMERAL_CACHE_CONTROL };
}

/**
 * The system prompt as a cached text block. An empty prompt keeps the string
 * form, since an empty text block is not cacheable.
 */
export function buildCachedSystem(instructions: string): unknown {
  if (!instructions.trim()) return instructions;
  return [markedWithCacheControl({ type: "text", text: instructions })];
}

/**
 * The tool definitions with a breakpoint on the last one. Copied rather than
 * marked in place so the caller's tool array stays byte-identical across hops.
 */
export function withCachedToolDefinitions(tools: readonly unknown[]): unknown[] {
  const marked = tools.slice();
  const lastIndex = marked.length - 1;
  const last = marked[lastIndex];
  if (!isRecord(last)) return marked;
  marked[lastIndex] = markedWithCacheControl(last);
  return marked;
}

function lastCacheableBlockIndex(content: readonly unknown[]): number {
  for (let index = content.length - 1; index >= 0; index -= 1) {
    if (acceptsCacheControl(content[index])) return index;
  }
  return -1;
}

/**
 * The transcript with the rolling breakpoint on its last cacheable block.
 * Copies down to the single block it marks, so the canonical conversation is
 * never written to.
 */
export function withRollingCacheBreakpoint(items: readonly unknown[]): unknown[] {
  const marked = items.slice();
  for (let index = marked.length - 1; index >= 0; index -= 1) {
    const message = marked[index];
    if (!isRecord(message) || !Array.isArray(message.content)) continue;
    const blockIndex = lastCacheableBlockIndex(message.content);
    if (blockIndex < 0) continue;
    const content = message.content.slice();
    content[blockIndex] = markedWithCacheControl(content[blockIndex] as Record<string, unknown>);
    marked[index] = { ...message, content };
    return marked;
  }
  return marked;
}
